import React, { useState, useRef, useEffect, useMemo } from 'react';
import { FixedSizeList } from 'react-window';
import {
  TextField,
  Paper,
  MenuItem,
  ListItemIcon,
  Checkbox,
  ListItemText,
  Stack
} from '@esgian/esgianui';
import './index.css';
import PropTypes from 'prop-types';
import { OuterBoxWithScrollbar } from '@components';

const TableMultiSelectFilter = ({ options = [], value = [], onChange }) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  const handleClick = (option) => {
    onChange(value.includes(option) ? value.filter((item) => item !== option) : [...value, option]);
  };

  const getDisplayValue = () => {
    if (value.length === 0) return '-';
    if (value.length === 1) return value[0];
    return `${value[0]} +${value.length - 1}`;
  };

  const filteredOptions = useMemo(() => {
    const safeOptions = options.filter((opt) => opt !== null);
    if (!searchTerm) return safeOptions;
    return safeOptions.filter((opt) => opt.toLowerCase().includes(searchTerm.toLowerCase()));
  }, [options, searchTerm]);

  const renderRow = ({ index, style }) => {
    const option = filteredOptions[index];
    const isSelected = value.includes(option);

    return (
      <MenuItem key={option} value={option} onClick={() => handleClick(option)} style={style}>
        <ListItemIcon>
          <Checkbox size="small" color="primary" checked={isSelected} />
        </ListItemIcon>
        <ListItemText
          primaryTypographyProps={{
            sx: { textOverflow: 'ellipsis', overflow: 'hidden' },
            variant: 'body2'
          }}
          primary={option}
        />
      </MenuItem>
    );
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div className="table-multi-select-filter" ref={dropdownRef}>
      <TextField
        InputProps={{
          readOnly: true,
          sx: { height: '39px' }
        }}
        variant="outlined"
        value={getDisplayValue()}
        onClick={() => setOpen(!open)}
        fullWidth
        sx={{ cursor: 'pointer' }}
      />

      {open && (
        <div
          className="dropdown"
          style={{ boxShadow: '0px 0px 4px 0px rgba(122,122,122,1)', width: '100%' }}>
          <Paper>
            <Stack spacing={1} p={1}>
              <TextField
                size="small"
                placeholder="Search..."
                autoFocus
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                fullWidth
              />
              <FixedSizeList
                outerElementType={OuterBoxWithScrollbar}
                height={Math.min(filteredOptions.length, 5) * 35}
                width="100%"
                itemSize={35}
                itemCount={filteredOptions.length}>
                {renderRow}
              </FixedSizeList>
            </Stack>
          </Paper>
        </div>
      )}
    </div>
  );
};

TableMultiSelectFilter.propTypes = {
  onChange: PropTypes.func.isRequired,
  value: PropTypes.arrayOf(PropTypes.string),
  options: PropTypes.arrayOf(PropTypes.string)
};

export default TableMultiSelectFilter;
